/**
 * Relationship Data Fetching Hooks
 *
 * Hooks for fetching schema relationships for the view builder.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import type { SchemaRelationshipResponse } from "../types";

const RELATIONSHIPS_API_BASE = "/api/workspace/myvault/schemas/relationships";

/**
 * Helper to create headers with workspace ID
 */
const getWorkspaceHeaders = (workspaceId: string): Record<string, string> => ({
  "x-workspace-id": workspaceId,
});

/**
 * Hook to fetch all relationships for a workspace.
 */
export const useRelationships = (workspaceId: string) => {
  const [relationships, setRelationships] = useState<
    SchemaRelationshipResponse[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authFetch = useAuthFetch();
  const authFetchRef = useRef(authFetch);

  useEffect(() => {
    authFetchRef.current = authFetch;
  }, [authFetch]);

  const fetchRelationships = useCallback(async () => {
    if (!workspaceId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await authFetchRef.current(
        RELATIONSHIPS_API_BASE,
        "fetch relationships",
        { headers: getWorkspaceHeaders(workspaceId) },
      );
      setRelationships(response.items || response || []);
      setError(null);
    } catch (err) {
      console.error("Relationships fetch error:", err);
      const message =
        err instanceof Error ? err.message : "Failed to fetch relationships";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchRelationships();
  }, [fetchRelationships]);

  return {
    relationships,
    loading,
    error,
    refresh: fetchRelationships,
  };
};

/**
 * Hook to get relationships for a specific table.
 * Returns relationships where the table is either the source or target.
 */
export const useTableRelationships = (
  workspaceId: string,
  tableId: string | null,
) => {
  const { relationships, loading, error, refresh } =
    useRelationships(workspaceId);

  const tableRelationships = tableId
    ? relationships.filter(
        (r) => r.from_table_id === tableId || r.to_table_id === tableId,
      )
    : [];

  return {
    relationships: tableRelationships,
    allRelationships: relationships,
    loading,
    error,
    refresh,
  };
};

/**
 * Build a relationship graph starting from a focus table.
 * Returns relationships organized for tree display.
 */
export interface RelationshipNode {
  relationship: SchemaRelationshipResponse;
  targetTableId: string;
  targetTableName?: string;
  children: RelationshipNode[];
}

export const buildRelationshipGraph = (
  relationships: SchemaRelationshipResponse[],
  focusTableId: string,
  excludeRelationshipIds: Set<string> = new Set(),
): RelationshipNode[] => {
  // Get direct relationships from focus table
  const directRelationships = relationships.filter(
    (r) =>
      !excludeRelationshipIds.has(r.id) &&
      (r.from_table_id === focusTableId || r.to_table_id === focusTableId),
  );

  return directRelationships.map((rel) => {
    const targetTableId =
      rel.from_table_id === focusTableId ? rel.to_table_id : rel.from_table_id;

    return {
      relationship: rel,
      targetTableId,
      children: [], // Children can be expanded on demand
    };
  });
};

/**
 * Get the "other" table ID from a relationship given one table ID.
 */
export const getOtherTableId = (
  relationship: SchemaRelationshipResponse,
  tableId: string,
): string => {
  return relationship.from_table_id === tableId
    ? relationship.to_table_id
    : relationship.from_table_id;
};

/**
 * Get display info for a relationship.
 */
export const getRelationshipDisplayInfo = (
  relationship: SchemaRelationshipResponse,
  sourceTableId: string,
  tables: { id: string; table_name: string; display_name?: string }[],
): {
  sourceTableName: string;
  targetTableName: string;
  relationshipType: string;
} => {
  const sourceTable = tables.find((t) => t.id === sourceTableId);
  const targetTableId = getOtherTableId(relationship, sourceTableId);
  const targetTable = tables.find((t) => t.id === targetTableId);

  return {
    sourceTableName:
      sourceTable?.display_name || sourceTable?.table_name || "Unknown",
    targetTableName:
      targetTable?.display_name || targetTable?.table_name || "Unknown",
    relationshipType: relationship.relationship_type || "MANY_TO_ONE",
  };
};
